import React, { useRef, useEffect, useState, ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TabScrollerProps {
  children: ReactNode;
  className?: string;
}

export const TabScroller: React.FC<TabScrollerProps> = ({ children, className }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth);
  };

  useEffect(() => {
    checkScroll();
  }, []);

  // Handle scroll events to show active scrolling indicator
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    
    const handleScroll = () => {
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      
      // Set new timeout to hide the indicator after scrolling stops
      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    };
    
    scrollContainer.addEventListener('scroll', handleScroll);
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  const scrollLeft = () => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
  };

  return (
    <div className={cn("relative", isScrolling && "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-gradient-to-r after:from-gold-400/0 after:via-gold-400/50 after:to-gold-400/0 after:animate-pulse")}>
      {showLeftArrow && (
        <button
          onClick={scrollLeft}
          className={cn(
            "absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1",
            "w-6 h-6 flex items-center justify-center",
            "bg-dark-200/80 backdrop-blur-sm rounded-full shadow-md",
            "text-dark-800 hover:text-white transition-colors",
            "border border-dark-300/50"
          )}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      
      {showRightArrow && (
        <button
          onClick={scrollRight}
          className={cn(
            "absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1",
            "w-6 h-6 flex items-center justify-center",
            "bg-dark-200/80 backdrop-blur-sm rounded-full shadow-md",
            "text-dark-800 hover:text-white transition-colors",
            "border border-dark-300/50",
            "animate-pulse"
          )}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
      
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className={cn(
          "overflow-x-auto scrollbar-none",
          className,
          showRightArrow && "mask-image-right"
        )}
      >
        {children}
      </div>
    </div>
  );
};